import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';

const ManualIdsInput = ({ value, onChange, onClear, usePhones, onUsePhonesChange }) => {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
 const handleInput = (e) => {
   const newValue = e.target.value;
   onChange(newValue);
 };

 const handleFileUpload = async (e) => {
   const file = e.target.files[0];
   if (!file) return;

   setIsProcessing(true);

   try {
     const fileExtension = file.name.split('.').pop().toLowerCase();
     
     if (fileExtension === 'csv' || fileExtension === 'xlsx' || fileExtension === 'xls') {
       const data = await readFile(file);
       const ids = parseFile(data, fileExtension);
       
       // Объединяем существующие ID с новыми (если есть)
       const existingIds = value ? value.split('\n').filter(line => line.trim()) : [];
       const allIds = [...existingIds, ...ids].filter((id, index, arr) => arr.indexOf(id) === index); // Убираем дубликаты
       
       onChange(allIds.join('\n'));
     } else {
       alert('Please upload a CSV or XLSX file');
     }
   } catch (error) {
     console.error('Error processing file:', error);
     alert('Error processing file. Please make sure the file has a column named "Ids"');
   } finally {
     setIsProcessing(false);
     // Сбрасываем input чтобы можно было загрузить тот же файл снова
     if (fileInputRef.current) {
       fileInputRef.current.value = '';
     }
   }
 };

 const readFile = (file) => {
   return new Promise((resolve, reject) => {
     const reader = new FileReader();
     
     reader.onload = (e) => {
       try {
         const data = e.target.result;
         resolve(data);
       } catch (error) {
         reject(error);
       }
     };
     
     reader.onerror = (error) => reject(error);
     
     if (file.name.endsWith('.csv')) {
       reader.readAsText(file);
     } else {
       reader.readAsArrayBuffer(file);
     }
   });
 };

 const parseFile = (data, fileExtension) => {
   let workbook;
   let sheetName;
   let worksheet;

   // Используем XLSX для парсинга как CSV, так и XLSX/XLS
   if (fileExtension === 'csv') {
     workbook = XLSX.read(data, { type: 'string' });
   } else {
     workbook = XLSX.read(data, { type: 'array' });
   }
   
   sheetName = workbook.SheetNames[0];
   worksheet = workbook.Sheets[sheetName];

   // Конвертируем в JSON с массивом массивов (header: 1)
   const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

   // Находим заголовок с колонкой "Ids" (ищем точное совпадение "ids" или "id")
   const headerRowIndex = jsonData.findIndex(row => 
     row.some(cell => {
       const cellValue = cell ? cell.toString().toLowerCase().trim() : '';
       return cellValue === 'ids' || cellValue === 'id';
     })
   );

   if (headerRowIndex === -1) {
     throw new Error('Column "Ids" not found in file. Please make sure the file has a column named "Ids"');
   }

   const headerRow = jsonData[headerRowIndex];
   const idsColumnIndex = headerRow.findIndex(cell => {
     const cellValue = cell ? cell.toString().toLowerCase().trim() : '';
     return cellValue === 'ids' || cellValue === 'id';
   });

   if (idsColumnIndex === -1) {
     throw new Error('Column "Ids" not found in file');
   }

   // Извлекаем данные из колонки Ids (той же колонки, где находится заголовок)
   const ids = [];
   const dataStartIndex = headerRowIndex + 1;
   
   for (let i = dataStartIndex; i < jsonData.length; i++) {
     const row = jsonData[i];
     if (row && row[idsColumnIndex] !== undefined && row[idsColumnIndex] !== null) {
       const id = row[idsColumnIndex].toString().trim();
       if (id) {
         ids.push(id);
       }
     }
   }

   if (ids.length === 0) {
     throw new Error('No data found in "Ids" column. Please make sure the column contains data below the header.');
   }

   return ids.filter(id => id); // Убираем пустые значения
 };

 const handleFileButtonClick = () => {
   fileInputRef.current?.click();
 };

 const validateIds = (ids) => {
   const lines = ids
     .split('\n')
     .map(line => line.trim())
     .filter(line => line); // пропускаем пустые строки

   if (usePhones) {
     // Для телефонов: принимаем любые символы (цифры, +, -, пробелы, скобки и т.д.)
     const validPhones = lines.filter(phone => phone.length > 0);
     return {
       invalidIds: [],
       validIds: validPhones
     };
   } else {
     // Для ID: только цифры
     const invalidIds = lines.filter(id => !(/^\d+$/.test(id)));
     const validIds = lines.filter(id => /^\d+$/.test(id));
     return {
       invalidIds,
       validIds
     };
   }
 };

 const { invalidIds, validIds } = validateIds(value);

return (
  <div className="mt-6">
    <div className="flex justify-between items-center mb-2">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <label className="block text-gray-700 text-sm font-bold">
            Manual User IDs
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={usePhones || false}
              onChange={(e) => onUsePhonesChange(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 font-medium">Use phones</span>
          </label>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {usePhones ? 'Enter phone numbers (one per line)' : 'Enter IDs (one per line)'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
        />
        <button
          onClick={handleFileButtonClick}
          disabled={isProcessing}
          className="text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add table
            </>
          )}
        </button>
        {value && (
          <button
            onClick={onClear}
            className="text-sm bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-md transition-colors duration-200"
          >
            Clear List
          </button>
        )}
      </div>
    </div>
     
     <textarea
       value={value}
       onInput={handleInput}
       className="w-full h-40 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
       placeholder={usePhones ? "Enter phone numbers here (one per line)" : "Enter IDs here (one per line)"}
     />
     
     <div className="mt-2 space-y-2">
       {!usePhones && invalidIds.length > 0 && (
         <p className="text-sm text-red-500">
           Some IDs are invalid (must contain only numbers):
           <br />
           {invalidIds.join(', ')}
         </p>
       )}
       <div className="flex justify-end items-center text-sm">
         {validIds.length > 0 && (
           <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded">
             {validIds.length} valid {usePhones ? 'phone numbers' : 'IDs'}
           </span>
         )}
       </div>
     </div>
   </div>
 );
};

export default ManualIdsInput;